import React, { useMemo, useState } from 'react';
import { GenericToast, ToastOrientations, useToast } from '@alucio/lux-ui';
import { DNAModalVariant } from '../DNAConnectedModal';
import { FolderORM } from 'src/types/orms';
import ActiveUser from 'src/state/global/ActiveUser';
import { useTenant } from 'src/state/redux/selector/tenant';
import { LabelValues, SharePermission, ShareTargetType, User } from '@alucio/aws-beacon-amplify/src/models';
import ShareFolder from './ShareFolder';
import CreateAccessFilter from './CreateAccessFilter';
import { v4 as uuid } from 'uuid';
import { PERMISSION_TYPE } from '@alucio/aws-beacon-amplify/src/API';
import { folderActions } from 'src/state/redux/slice/folder';
import { useDispatch } from 'react-redux';
import UnshareWarning from './UnshareWarning';
import { DeviceMode, useAppSettings } from 'src/state/context/AppSettings';
import AddPeople from './AddPeople/AddPeople';

export type AccessFilterType = {
  [x: string]: LabelValues;
}
export interface ShareFilter {
  id: string,
  filter: User | AccessFilterType | 'ALL',
  targetType: ShareTargetType,
  // TO KNOW WHETHER WE NEED TO UPDATE
  // THE UPDATED/CREATED AT OR NOT
  // (FOR ALREADY EXISTING FILTERS)
  createdAt?: string,
  updatedAt?: string,
  isDeleted: boolean,
  deletedAt?: string,
  deletedBy?: string,
}

const filterToString = (filter: AccessFilterType) => {
  return Object.keys(filter).map((key) => {
    return filter[key].key + '-' + filter[key].values.join(',');
  }).join(',');
}

export const isEveryoneType = (item: User | AccessFilterType | 'ALL') : item is 'ALL' => {
  return item === 'ALL';
}

export const isUserType = (item: User | AccessFilterType | 'ALL'): item is User => {
  return !isEveryoneType(item) && Object.keys(item).includes('id');
}

export const isFilterType = (item: User | AccessFilterType | 'ALL'): item is AccessFilterType => {
  return !isEveryoneType(item) && !Object.keys(item).includes('id');
}

export enum ShareFolderMode {
  CREATE_ACCESS_FILTER,
  SHARE_FOLDER,
  UNSHARE,
  ADD_PEOPLE,
}

const DNAShareFolderModal: DNAModalVariant<{ folderORM: FolderORM }> = (props) => {
  const tenant = useTenant(ActiveUser.user!.tenantId);
  const { deviceMode } = useAppSettings();
  const isDesktop = deviceMode === DeviceMode.desktop;
  const tenantUsers = tenant?.meta.users || [];
  const { folderORM, toggleModal } = props;
  const [currentItems, setCurrentItems] = useState<ShareFilter[]>(initializeCurrentItems())
  const initialValues = useMemo(() => currentItems, []);
  const toast = useToast()
  const dispatch = useDispatch();
  const [mode, setMode] = useState<ShareFolderMode>(ShareFolderMode.SHARE_FOLDER);

  const handleAddItem = (item: User | AccessFilterType | 'ALL') => {
    const itemExists = currentItems.find((shareFilter) => {
      if (isEveryoneType(shareFilter.filter) && isEveryoneType(item)) {
        return shareFilter.targetType === ShareTargetType.ALL && !shareFilter.isDeleted;
      }
      else if (isUserType(shareFilter.filter) && isUserType(item)) {
        return item.id === shareFilter.filter.id && !shareFilter.isDeleted;
      }
      else if (isFilterType(shareFilter.filter) && isFilterType(item)) {
        return filterToString(shareFilter.filter) === filterToString(item) && !shareFilter.isDeleted;
      }
    })

    if (itemExists) {
      const textError = isUserType(item) ? 'User already on the list' : 'Filter already on the list'
      toast.add(
        <GenericToast
          title={textError}
          status="error"
        />,
        ToastOrientations.TOP_RIGHT,
      )
      setMode(ShareFolderMode.SHARE_FOLDER);
      return
    }

    if (isUserType(item)) {
      setCurrentItems((prev) => [...prev, {
        id: uuid(),
        targetType: ShareTargetType.USER,
        filter: item,
        isDeleted: false,
      }])
    }

    else if (isFilterType(item)) {
      setCurrentItems((prev) => [{
        id: uuid(),
        targetType: ShareTargetType.FILTER,
        filter: item,
        isDeleted: false,
      }, ...prev])
    }

    else if (isEveryoneType(item)) {
      setCurrentItems((prev) => [{
        id: uuid(),
        targetType: ShareTargetType.ALL,
        filter: 'ALL',
        isDeleted: false,
      }, ...prev])
    }
    setMode(ShareFolderMode.SHARE_FOLDER);
  }

  const handleRemoveItem = (idToRemove: string) => {
    if (initialValues.find((item) => item.id === idToRemove)) {
      setCurrentItems((prev) => prev.map((item) => {
        return {
          ...item,
          isDeleted: item.id === idToRemove ? true : item.isDeleted,
          deletedAt: item.id === idToRemove ? new Date().toISOString() : item.deletedAt,
          deletedBy: item.id === idToRemove ? ActiveUser.user!.id : item.deletedBy,
        }
      }));
    }
    else {
      setCurrentItems((prev) => prev.filter((item) => item.id !== idToRemove));
    }
  }

  const handleShareWithEveryone = () => {
    handleAddItem('ALL');
  }

  function initializeCurrentItems() {
    const items = folderORM.model.sharePermissions?.map((permission) => {
      const user = tenantUsers.find(({ id }) => id === permission.targetUsername);
      const filterObj = {}

      if (permission.targetLabels?.length) {
        permission.targetLabels.forEach((label) => {
          filterObj[label.key] = {
            key: label.key,
            values: label.values,
          }
        })
      }
      return {
        id: permission.id,
        targetType: permission.targetType,
        filter: permission.targetType === ShareTargetType.ALL ? 'ALL' : (user || filterObj),
        isDeleted: permission.isDeleted,
      }
    }) || [];
    return items as ShareFilter[]
  }

  const handleUnshare = () => {
    const sharePermissions: SharePermission[] = currentItems?.map((item) => {
      const now = new Date().toISOString();
      const isUser = isUserType(item.filter);
      const sharePermission: SharePermission = {
        id: item.id,
        isDeleted: true,
        createdAt: item.createdAt || now,
        updatedAt: item.updatedAt || now,
        createdBy: ActiveUser.user!.id,
        updatedBy: ActiveUser.user!.id,
        targetUsername: isUserType(item.filter) ? item.filter.id : undefined,
        targetType: isUser ? ShareTargetType.USER : ShareTargetType.FILTER,
        type: PERMISSION_TYPE.VIEW,
        deletedAt: now,
        deletedBy: ActiveUser.user!.id,
      };

      return sharePermission;
    }) || [];

    analytics?.track('FOLDER_UNSHARE_ALL', {
      action: 'UNSHARE_ALL',
      category: 'FOLDER',
      folderId: folderORM.model.id,
    });
    dispatch(folderActions.updateSharePermission(sharePermissions, folderORM.model));
    toggleModal();
  }

  const handleConfirm = () => {
    const now = new Date().toISOString();
    if (!currentItems.length) {
      return;
    }

    const sharePermissions: SharePermission[] = currentItems.map((item) => {
      const sharePermission: SharePermission = {
        id: item.id,
        isDeleted: item.isDeleted,
        createdAt: item.createdAt || now,
        updatedAt: item.updatedAt || now,
        createdBy: ActiveUser.user!.id,
        updatedBy: ActiveUser.user!.id,
        targetUsername: isUserType(item.filter) ? item.filter.id : undefined,
        targetType: item.targetType,
        type: PERMISSION_TYPE.VIEW,
        targetLabels: [],
        deletedAt: item.deletedAt,
        deletedBy: item.deletedBy,
      };

      if (isFilterType(item.filter)) {
        Object.keys(item.filter).forEach((key) => {
          sharePermission.targetLabels?.push({
            key,
            values: item.filter[key].values,
          });
        });
      }
      return sharePermission;
    }) || [];

    analytics?.track('FOLDER_SHARE_SAVE', {
      action: 'SHARE_SAVE',
      category: 'FOLDER',
      folderId: folderORM.model.id,
    });
    dispatch(folderActions.updateSharePermission(sharePermissions, folderORM.model));
    toggleModal()
  }

  return (
    <>
      <ShareFolder
        {...props}
        setMode={setMode}
        currentItems={currentItems}
        onRemoveItem={handleRemoveItem}
        onConfirm={handleConfirm}
        onShareWithEveryone={handleShareWithEveryone}
        initialValues={initialValues}
        isDesktop={isDesktop}
        display={mode === ShareFolderMode.SHARE_FOLDER}
      />
      <AddPeople
        {...props}
        setMode={setMode}
        display={mode === ShareFolderMode.ADD_PEOPLE}
        currentItems={currentItems}
        onAddItem={handleAddItem}
      />
      <CreateAccessFilter
        {...props}
        folderName={folderORM.model.name}
        setMode={setMode}
        isDesktop={isDesktop}
        onCreateFilter={handleAddItem}
        display={mode === ShareFolderMode.CREATE_ACCESS_FILTER}
      />
      <UnshareWarning
        {...props}
        setMode={setMode}
        display={mode === ShareFolderMode.UNSHARE}
        onUnshare={handleUnshare}
      />
    </>
  )
}

export default DNAShareFolderModal;
